import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Logo } from './Logo';
import { ThemeToggle, LanguageSelector } from './Controls';
import { useI18n } from '../i18n/I18nContext';
import { useAuth } from '../contexts/AuthContext';
import { api, fmtUsd } from '../lib/api';

export function Ticker() {
  const [coins, setCoins] = useState([]);
  useEffect(() => {
    let active = true;
    const load = () => api.get('/markets/top?limit=10').then(({ data }) => active && setCoins(data.coins)).catch(() => {});
    load();
    const id = setInterval(load, 45000);
    return () => { active = false; clearInterval(id); };
  }, []);
  if (!coins.length) return null;
  const row = [...coins, ...coins];
  return (
    <div className="w-full overflow-hidden border-b border-border bg-vj-charcoal/60 backdrop-blur-sm" data-testid="live-ticker">
      <div className="flex vj-ticker-track whitespace-nowrap py-2">
        {row.map((c, i) => {
          const up = (c.price_change_percentage_24h || 0) >= 0;
          return (
            <span key={i} className="inline-flex items-center gap-2 px-5 text-xs font-mono">
              <span className="font-semibold text-foreground">{c.symbol}</span>
              <span className="text-vj-grey">{fmtUsd(c.current_price, c.current_price < 1 ? 4 : 2)}</span>
              <span className={up ? 'text-vj-neon' : 'text-vj-red'}>
                {up ? '▲' : '▼'} {Math.abs(c.price_change_percentage_24h || 0).toFixed(2)}%
              </span>
            </span>
          );
        })}
      </div>
    </div>
  );
}

export function PublicNav() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const links = [
    { to: '/markets', label: t('nav.markets') },
    { to: '/si-bot', label: t('nav.sibot') },
    { to: '/how-it-works', label: t('nav.howItWorks') },
    { to: '/faq', label: t('nav.faq') },
    { to: '/support', label: t('nav.support') },
  ];
  return (
    <header className="fixed top-0 inset-x-0 z-50">
      <Ticker />
      <nav className="backdrop-blur-xl bg-vj-obsidian/80 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Logo size={34} />
            <div className="hidden lg:flex items-center gap-6">
              {links.map((l) => (
                <Link key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`}
                  className="text-sm text-vj-silver/80 hover:text-vj-neon transition-colors duration-200">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <LanguageSelector />
            <ThemeToggle />
            {user ? (
              <button onClick={() => navigate('/dashboard')} data-testid="nav-dashboard-btn"
                className="hidden sm:inline-flex h-9 px-4 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm items-center hover:-translate-y-0.5 transition-all vj-glow-green">
                {t('nav.dashboard')}
              </button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} data-testid="nav-login-btn"
                  className="hidden sm:inline-flex h-9 px-4 rounded-lg border border-border text-foreground text-sm items-center hover:border-vj-neon/50 transition-all">
                  {t('nav.login')}
                </button>
                <button onClick={() => navigate('/register')} data-testid="nav-register-btn"
                  className="hidden sm:inline-flex h-9 px-4 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm items-center hover:-translate-y-0.5 transition-all vj-glow-green">
                  {t('nav.getStarted')}
                </button>
              </>
            )}
            <button className="lg:hidden w-9 h-9 grid place-items-center text-foreground" onClick={() => setOpen(!open)} data-testid="mobile-menu-btn">
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
        {open && (
          <div className="lg:hidden border-t border-border bg-vj-obsidian/95 px-4 py-4 space-y-2">
            {links.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2 text-sm text-vj-silver/80">{l.label}</Link>
            ))}
            <div className="flex gap-2 pt-2">
              <button onClick={() => navigate(user ? '/dashboard' : '/login')} className="flex-1 h-9 rounded-lg border border-border text-sm">{user ? t('nav.dashboard') : t('nav.login')}</button>
              {!user && <button onClick={() => navigate('/register')} className="flex-1 h-9 rounded-lg bg-vj-neon text-vj-obsidian font-semibold text-sm">{t('nav.getStarted')}</button>}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export function Footer() {
  const { t } = useI18n();
  const cols = [
    { h: 'Platform', links: [['Markets', '/markets'], ['S.I. Bot', '/si-bot'], ['How It Works', '/how-it-works'], ['FAQ', '/faq']] },
    { h: 'Legal', links: [['Terms of Service', '/terms'], ['Privacy Policy', '/privacy'], ['Risk Disclosure', '/risk-disclosure']] },
    { h: 'Account', links: [['Log In', '/login'], ['Register', '/register'], ['Support', '/support']] },
  ];
  return (
    <footer className="border-t border-border bg-vj-obsidian mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 grid gap-10 md:grid-cols-4">
        <div className="space-y-4">
          <Logo size={40} />
          <p className="text-sm text-vj-grey max-w-xs">{t('brand.tagline')}. Automation-first cryptocurrency trading with transparent account management.</p>
        </div>
        {cols.map((c) => (
          <div key={c.h}>
            <h4 className="font-display text-sm font-semibold text-vj-gold tracking-wide mb-4">{c.h}</h4>
            <ul className="space-y-2">
              {c.links.map(([l, to]) => (
                <li key={to}><Link to={to} className="text-sm text-vj-grey hover:text-vj-neon transition-colors">{l}</Link></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-border py-6 px-6 text-center">
        <p className="text-xs text-vj-grey max-w-3xl mx-auto">
          © {new Date().getFullYear()} VEYJEX Global Trading Exchange. Trading cryptocurrency involves substantial risk of loss. VEYJEX does not guarantee returns.
          Legal content is draft pending professional review. Live exchange execution and on-chain withdrawal signing operate in development mode until production integrations are configured.
        </p>
      </div>
    </footer>
  );
}
